import React from 'react';
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, Empty, Spin } from 'antd';
import { useLanguage } from '@/contexts/LanguageContext';
import { DownloadOutlined, FileTextOutlined, FileImageOutlined } from '@ant-design/icons';
import { exportToCSV, exportElementAsImage } from '@/utils/exportUtils';
import { Button, Dropdown } from 'antd';

const CustomXAxisTick = ({ x, y, payload }) => {
    if (!payload || !payload.value) return null;

    const value = payload.value;
    const limit = 17;
    let lines = [];

    if (value.length > limit) {
        // Find a space near the limit to split
        const lastSpace = value.lastIndexOf(' ', limit);
        if (lastSpace !== -1) {
            lines = [value.substring(0, lastSpace), value.substring(lastSpace + 1)];
        } else {
            // Split at the limit if no space
            lines = [value.substring(0, limit), value.substring(limit)];
        }
    } else {
        lines = [value];
    }

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={16}
                textAnchor="end"
                fill="#666"
                transform="rotate(-45)"
                style={{ fontSize: '12px' }}
            >
                {lines.map((line, index) => (
                    <tspan x={0} dy={index === 0 ? 0 : 14} key={index}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const BarChartComponent = ({
    data,
    xAxisKey,
    yAxisKey,
    title,
    color = '#3b82f6',
    loading = false,
    height = 450,
    allowExport = true,
    onClick = null
}) => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();

    if (loading) {
        return (
            <Card title={title}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    const chartId = `bar-chart-${title?.replace(/\s+/g, '-').toLowerCase()}`;

    const items = [
        {
            key: 'csv',
            label: t('common.exportCSV') || 'CSV',
            icon: <FileTextOutlined />,
            onClick: () => {
                const csvData = data.map(item => ({
                    [xAxisKey]: item[xAxisKey],
                    [yAxisKey]: item[yAxisKey]
                }));
                exportToCSV(csvData, title || 'analytics_data');
            }
        },
        {
            key: 'image',
            label: t('common.exportImage') || 'Image',
            icon: <FileImageOutlined />,
            onClick: () => exportElementAsImage(chartId, title || 'chart')
        }
    ];

    if (!data || data.length === 0) {
        return (
            <Card title={title}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
                    <Empty description={t('common.noData')} />
                </div>
            </Card>
        );
    }

    return (
        <Card
            id={chartId}
            title={title}
            extra={allowExport && (
                <Dropdown menu={{ items }} placement="bottomRight">
                    <Button
                        type="text"
                        icon={<DownloadOutlined />}
                    >
                        {t('common.export')}
                    </Button>
                </Dropdown>
            )}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart 
                    data={data} 
                    margin={{ top: 10, right: 30, left: 0, bottom: 50 }}
                    onClick={(data) => onClick && onClick(data)}
                    style={{ cursor: onClick ? 'pointer' : 'default' }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey={xAxisKey}
                        height={80}
                        interval={0}
                        tickMargin={isRTL ? 65 : 10}
                        tick={<CustomXAxisTick />}
                    />
                    <YAxis
                        tickMargin={10}
                        width={60}
                        orientation={"left"}
                    />
                    <Tooltip />
                    <Legend
                        verticalAlign="top"
                        height={36}
                        wrapperStyle={{ paddingBottom: '20px' }}
                        formatter={(value) => (
                            <span style={{
                                marginLeft: isRTL ? 0 : 8,
                                marginRight: isRTL ? 8 : 0,
                                color: '#666'
                            }}>
                                {value}
                            </span>
                        )}
                    />
                    <Bar dataKey={yAxisKey} fill={color} radius={[8, 8, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};

export default BarChartComponent;
